import React, { useState, useMemo } from 'react';
import { BarChart3, Calendar, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const GraficosTempoDashboard = ({ data }) => {
  const [tipoTempo, setTipoTempo] = useState('por_tempo');

  // Opções de tempo
  const opcoesTempo = [
    { id: 'por_dia', label: 'Por Dia', icon: Calendar, cor: 'blue' },
    { id: 'por_tempo', label: 'Por Tempo', icon: Clock, cor: 'orange' },
    { id: 'por_mes', label: 'Por Mês', icon: Calendar, cor: 'green' }
  ];

  // Processar dados baseado no tipo de tempo selecionado
  const dadosProcessados = useMemo(() => {
    // Extrair dados dentro do useMemo para evitar dependências externas
    const porDia = data.por_dia?.oss || [];
    const porTempo = data.por_tempo?.oss || [];
    const porMes = data.por_mes?.oss || [];
    
    let dados = [];
    let titulo = '';
    let cor = 'var(--accent)';

    switch (tipoTempo) {
      case 'por_dia':
        // Se os dados já estão agregados (têm TOTAL), usar diretamente
        // Caso contrário, agrupar por fechamento somando os valores de OS
        if (porDia.length > 0 && porDia[0].TOTAL !== undefined) {
          // Dados já agregados
          dados = porDia
            .sort((a, b) => {
              const diaA = parseInt(a.fechamento || '0');
              const diaB = parseInt(b.fechamento || '0');
              return diaA - diaB;
            })
            .map(item => ({
              name: item.fechamento || 'N/A',
              value: item.TOTAL || 0
            }));
        } else {
          // Agrupar por fechamento somando os valores de OS
          const agrupados = {};
          
          porDia.forEach(item => {
            const fechamento = item.fechamento || 'N/A';
            const os = parseInt(item.OS || 0);
            
            if (!agrupados[fechamento]) {
              agrupados[fechamento] = 0;
            }
            
            agrupados[fechamento] += os;
          });
          
          // Converter para array e ordenar por dia
          dados = Object.entries(agrupados)
            .map(([fechamento, total]) => ({
              name: fechamento,
              value: total
            }))
            .sort((a, b) => {
              const diaA = parseInt(a.name || '0');
              const diaB = parseInt(b.name || '0');
              return diaA - diaB;
            });
        }
        titulo = 'OSS por Dia';
        cor = 'var(--accent)';
        break;

      case 'por_tempo':
        if (porTempo.length > 0) {
          // Agregar valores de tempo de todos os assuntos
          const temposAgregados = {
            'ATÉ 4H': 0,
            'DE 4H À 8H': 0,
            'DE 8H À 12H': 0,
            'DE 12H À 24H': 0,
            'DE 24H À 48H': 0,
            'DE 48H À 72H': 0,
            'MAIOR QUE 72H': 0
          };
          
          // Somar valores de todos os itens
          porTempo.forEach(item => {
            temposAgregados['ATÉ 4H'] += parseInt(item['ATÉ 4H'] || 0);
            temposAgregados['DE 4H À 8H'] += parseInt(item['DE 4H À 8H'] || 0);
            temposAgregados['DE 8H À 12H'] += parseInt(item['DE 8H À 12H'] || 0);
            temposAgregados['DE 12H À 24H'] += parseInt(item['DE 12H À 24H'] || 0);
            temposAgregados['DE 24H À 48H'] += parseInt(item['DE 24H À 48H'] || 0);
            temposAgregados['DE 48H À 72H'] += parseInt(item['DE 48H À 72H'] || 0);
            temposAgregados['MAIOR QUE 72H'] += parseInt(item['MAIOR QUE 72H'] || 0);
          });
          
          dados = [
            { name: 'Até 4h', value: temposAgregados['ATÉ 4H'] },
            { name: '4h a 8h', value: temposAgregados['DE 4H À 8H'] },
            { name: '8h a 12h', value: temposAgregados['DE 8H À 12H'] },
            { name: '12h a 24h', value: temposAgregados['DE 12H À 24H'] },
            { name: '24h a 48h', value: temposAgregados['DE 24H À 48H'] },
            { name: '48h a 72h', value: temposAgregados['DE 48H À 72H'] },
            { name: 'Maior que 72h', value: temposAgregados['MAIOR QUE 72H'] }
          ];
          // Não ordenar por valor, manter ordem cronológica
        }
        titulo = 'OSS por Tempo de Resposta';
        cor = 'var(--status-warn)';
        break;

      case 'por_mes':
        // Agrupar por mês somando os valores de TOTAL
        const mesesAgrupados = {};
        
        porMes.forEach(item => {
          // Normalizar campo Mês (pode vir como MES, Mês, ou mes)
          const mes = item.MES || item.Mês || item.mes || 'N/A';
          const total = parseInt(item.TOTAL || 0);
          
          if (!mesesAgrupados[mes]) {
            mesesAgrupados[mes] = 0;
          }
          
          mesesAgrupados[mes] += total;
        });
        
        // Converter para array e ordenar por ano e mês
        dados = Object.entries(mesesAgrupados)
          .map(([mes, total]) => ({
            name: mes,
            value: total
          }))
          .sort((a, b) => {
            // Converter formato MM/YYYY ou YYYY-MM para Date para ordenação correta considerando ano e mês
            const parseData = (dataStr) => {
              if (!dataStr) return new Date(0);
              // Formato pode ser "01/2026" ou "2026-01" ou similar
              if (dataStr.includes('/')) {
                const [mes, ano] = dataStr.split('/');
                return new Date(parseInt(ano), parseInt(mes) - 1, 1);
              } else if (dataStr.includes('-')) {
                const [ano, mes] = dataStr.split('-');
                return new Date(parseInt(ano), parseInt(mes) - 1, 1);
              }
              return new Date(0);
            };
            
            const dateA = parseData(a.name);
            const dateB = parseData(b.name);
            return dateA - dateB;
          });
        titulo = 'OSS por Mês';
        cor = 'var(--status-good)';
        break;

      default:
        dados = [];
        titulo = 'Selecione um tipo de tempo';
    }

    return { dados, titulo, cor };
  }, [tipoTempo, data]);

  return (
    <div className="space-y-6">
      {/* Seletor de Tipo de Tempo */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-[color:var(--accent)]" />
          Gráficos de Tempo
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {opcoesTempo.map((opcao) => {
            const Icon = opcao.icon;
            const isActive = tipoTempo === opcao.id;
            
            return (
              <button
                key={opcao.id}
                onClick={() => setTipoTempo(opcao.id)}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  isActive
                    ? 'border-[color:var(--accent)] bg-[color:var(--accent)] text-white'
                    : 'border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <Icon className={`w-6 h-6 ${isActive ? 'text-white' : 'text-[color:var(--accent)]'}`} />
                  <span className="text-sm font-medium text-center">{opcao.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Gráfico de Barras Verticais */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)]">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4">
            {dadosProcessados.titulo}
            <span className="ml-2 text-sm text-[color:var(--text-muted)]">
              ({dadosProcessados.dados.length} registros)
            </span>
          </h3>
          
          {dadosProcessados.dados.length > 0 ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={dadosProcessados.dados}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                    tick={{ fill: 'var(--text-muted)' }}
                  />
                  <YAxis tick={{ fill: 'var(--text-muted)' }} />
                  <Tooltip 
                    formatter={(value, _name) => [value.toLocaleString('pt-BR'), 'Quantidade']}
                    labelFormatter={(label) => {
                      const labels = {
                        'por_dia': 'Dia',
                        'por_tempo': 'Tempo',
                        'por_mes': 'Mês'
                      };
                      return `${labels[tipoTempo] || 'Categoria'}: ${label}`;
                    }}
                    contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
                    labelStyle={{ color: 'var(--text-primary)' }}
                  />
                  <Bar 
                    dataKey="value" 
                    fill={dadosProcessados.cor}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-12 text-[color:var(--text-muted)]">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[color:var(--text-muted)]" />
              <p>Nenhum dado disponível para este tipo de tempo</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraficosTempoDashboard;


